import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { AnimatePresence, motion } from 'framer-motion';
import { TargetIcon } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';

import { parseAxiosError } from '@/api/api-axios';
import { useSubmitAboutYouSurveyMutation } from '@/api/api-hooks';
import { ButtonRadioGroup, ButtonRadioOption } from '@/components/ButtonRadioGroup';
import { FormLabelWithMessage } from '@/components/Forms/FormLabelWithMessage';
import { PublicHeader } from '@/components/Public/PublicHeader';
import { PublicLayout } from '@/components/Public/PublicLayout';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Form, FormControl, FormField, FormItem, FormLabel } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { useToast } from '@/components/ui/use-toast';

export const Route = createFileRoute('/$organizationName/about-you')({
  component: Signup,
});

const TYPE_OF_WORK_OPTIONS: ButtonRadioOption[] = [
  { value: 'Software Engineering' },
  { value: 'Systems Engineering' },
  { value: 'Hardware Engineering' },
  { value: 'Technical Writing' },
  { value: 'Customer Support' },
  { value: 'Product Management' },
  { value: 'Other' },
];

const TEAM_SIZE_OPTIONS: ButtonRadioOption[] = [
  { value: 'Just me' },
  { value: '2-10' },
  { value: '11-50' },
  { value: '51-150' },
  { value: 'More than 150' },
];

const PLANS_FOR_DRIVER_OPTIONS: ButtonRadioOption[] = [
  { value: 'To get up to speed on a codebase' },
  { value: 'To modernize or maintain legacy code' },
  { value: 'To maintain customer-facing documentation' },
  { value: 'To use with AI coding tools and agents' },
];

const PLAN_BLURBS: Record<string, { text: string; href?: string }> = {
  'To get up to speed on a codebase': {
    text: 'Customers see their engineering teams onboard twice as fast when using Driver.',
    href: 'https://driver.dev',
  },
  'To modernize or maintain legacy code': {
    text: 'Customers see 75% or faster legacy system discovery phase on their teams when using Driver. ',
    href: 'https://driver.dev',
  },
  'To maintain customer-facing documentation': {
    text: 'Driver speeds up your documentation process by up to 90%, no matter what codebase you throw at it.',
    href: 'https://driver.dev',
  },
  'To use with AI coding tools and agents': {
    text: 'Early tests of Driver MCP are benchmarking 10x faster agent task completion, 80% higher agent task acceptance, 80% reduction in hallucinations, and a reduction in overall token usage.',
    href: 'https://driver.dev',
  },
};

type AboutYouFormValues = {
  plansForDriver: string[];
  teamSize: string;
  typeOfWork: string;
  typeOfWorkOther: string;
};

function Signup() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isSubmitted, setIsSubmitted] = useState(false);

  const form = useForm<AboutYouFormValues>({
    defaultValues: {
      plansForDriver: [],
      teamSize: '',
      typeOfWork: '',
      typeOfWorkOther: '',
    },
    mode: 'onSubmit',
  });

  const typeOfWork = form.watch('typeOfWork');
  const plansForDriver = form.watch('plansForDriver');

  const { error, isError, mutateAsync: submitAboutYou } = useSubmitAboutYouSurveyMutation();

  const onSubmit = async (values: AboutYouFormValues) => {
    await submitAboutYou(values);
    setIsSubmitted(true);
    toast({ title: 'Success', description: 'Your answers were saved. Redirecting…', duration: 3000 });
    await new Promise((resolve) => setTimeout(resolve, 3000));
    navigate({ to: '/' });
  };

  const onSkip = async () => {
    // Submit an empty survey with skipped=true before redirecting
    try {
      await submitAboutYou({ plansForDriver: [], teamSize: '', typeOfWork: '', typeOfWorkOther: '', skipped: true });
    } catch (error) {
      // Just redirect if they've already submitted a survey.
      const errorMessage = parseAxiosError(error, 'An error occurred while submitting your response.');
      if (errorMessage === 'Survey already exists for this user. Cannot submit multiple surveys.') {
        navigate({ to: '/' });
      } else {
        return;
      }
    }
    navigate({ to: '/' });
  };

  const errorMessage = parseAxiosError(error, 'An error occurred while submitting your response.');

  return (
    <PublicLayout>
      <div className="mx-auto w-full max-w-[480px]">
        <PublicHeader title="Tell us about yourself" subtitle="We’ll use this to tailor Driver to your priorities." />
        <Form {...form}>
          <form className="mt-8 space-y-4" onSubmit={form.handleSubmit(onSubmit)}>
            <FormField
              control={form.control}
              name="typeOfWork"
              rules={{ required: '* required' }}
              render={({ field }) => (
                <FormItem className="space-y-1.5">
                  <FormLabelWithMessage
                    label="What best describes your work?"
                    messageClassName="!text-xs !leading-none"
                  />
                  <FormControl>
                    <ButtonRadioGroup
                      options={TYPE_OF_WORK_OPTIONS}
                      value={field.value}
                      onValueChange={field.onChange}
                      name="typeOfWork"
                      orientation="horizontal"
                      variant="outline"
                      size="xs"
                    />
                  </FormControl>
                  <AnimatePresence initial={false} mode="wait">
                    {typeOfWork === 'Other' && (
                      <motion.div
                        key="typeOfWorkOther"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.15, ease: 'easeInOut' }}
                        className="overflow-hidden"
                      >
                        <div className="pt-0.5">
                          <FormField
                            control={form.control}
                            name="typeOfWorkOther"
                            render={({ field: otherField }) => (
                              <FormItem>
                                <FormLabel className="sr-only">Please specify your work</FormLabel>
                                <FormControl>
                                  <Input
                                    id="typeOfWorkOther"
                                    placeholder="Please specify"
                                    inputSize="xs"
                                    className="max-w-[80%]"
                                    value={otherField.value}
                                    onChange={otherField.onChange}
                                  />
                                </FormControl>
                              </FormItem>
                            )}
                          />
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="teamSize"
              rules={{ required: '* required' }}
              render={({ field }) => (
                <FormItem className="space-y-1.5">
                  <FormLabelWithMessage label="How big is your team?" messageClassName="!text-xs !leading-none" />
                  <FormControl>
                    <ButtonRadioGroup
                      options={TEAM_SIZE_OPTIONS}
                      value={field.value}
                      onValueChange={field.onChange}
                      name="teamSize"
                      orientation="horizontal"
                      variant="outline"
                      size="xs"
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="plansForDriver"
              rules={{ validate: (val) => (val && val.length > 0 ? true : '* required') }}
              render={({ field }) => (
                <FormItem className="space-y-1.5">
                  <FormLabelWithMessage
                    label="How do you plan to use Driver?"
                    messageClassName="!text-xs !leading-none"
                  />
                  <FormControl>
                    <ButtonRadioGroup
                      options={PLANS_FOR_DRIVER_OPTIONS}
                      values={field.value}
                      onValuesChange={field.onChange}
                      name="plansForDriver"
                      orientation="vertical"
                      variant="outline"
                      size="xs"
                      selection="multiple"
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <AnimatePresence initial={false} mode="wait">
              {plansForDriver.length > 0 && (
                <motion.div
                  key="plans-alert"
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.15, ease: 'easeInOut' }}
                  className="!mt-0 overflow-hidden"
                >
                  <div className="pt-8">
                    <Alert className="bg-muted flex min-h-[76px] items-center justify-center gap-2 [&>svg]:top-[50%] [&>svg]:translate-y-[-50%] [&>svg+div]:translate-y-0">
                      <TargetIcon className="h-4 w-4" />
                      <AlertDescription className="text-xs">
                        {(() => {
                          const key = plansForDriver[plansForDriver.length - 1];
                          const blurb = PLAN_BLURBS[key];
                          if (!blurb) return null;
                          return (
                            <AnimatePresence initial={false} mode="wait">
                              <motion.span
                                key={key}
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.15 }}
                              >
                                {blurb.text}
                                {/* <a className="underline" href={blurb.href} target="_blank" rel="noreferrer">
                                  Learn more.
                                </a> */}
                              </motion.span>
                            </AnimatePresence>
                          );
                        })()}
                      </AlertDescription>
                    </Alert>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            <Button className="!mt-8 w-full" type="submit" disabled={form.formState.isSubmitting || isSubmitted}>
              {form.formState.isSubmitting || isSubmitted ? 'Saving…' : 'Continue'}
            </Button>
            {isError && <p className="center text-destructive !mb-4 text-sm">{errorMessage}</p>}
          </form>
        </Form>

        <p className="text-muted-foreground mt-0.5 text-center text-sm">
          <Button
            className="text-foreground hover:text-foreground/80 font-medium underline underline-offset-4"
            variant="link"
            onClick={onSkip}
          >
            Skip this step
          </Button>
        </p>
      </div>
    </PublicLayout>
  );
}
